import {
  IRoutingLayerModel,
  IRoutingLayersCollection,
  IPolygonsLayersCollection,
  IPolygonsLayerModel,
  ELocationLayerIcon,
  ILocationsLayersCollection,
  ILocationsLayerModel
} from 'features/RoutingLayers/types'
import { GetAllActiveRoutingLayersQuery, RoutingLayer, RoutingLayerType } from 'gql/generated/graphql'
import { TRGBAColor, TRGBColor } from 'types/maps'

export const createRoutingLayerModel = (routingLayer: RoutingLayer): IRoutingLayerModel => {
  return {
    id: routingLayer.id,
    name: routingLayer.name,
    type: routingLayer.type,
    createdDate: new Date(routingLayer.createdAt),
    isActive: routingLayer.isActive
  }
}

export const createRoutingLayersCollection = (routingLayers: RoutingLayer[]): IRoutingLayersCollection => {
  return {
    items: routingLayers.map(createRoutingLayerModel)
  }
}

const DEFAULT_COLOR: TRGBColor = [57, 57, 57]
export const getLayerColor = (color: string | null): TRGBColor => {
  if (color) {
    try {
      const colorParsed = JSON.parse(color) as TRGBColor
      return colorParsed
    } catch (e) {
      console.error('Error parsing color:', e)
    }
  }
  return DEFAULT_COLOR
}

export const createPolygonsLayerModel = (
  layer: GetAllActiveRoutingLayersQuery['routingLayers']['items'][0]
): IPolygonsLayerModel => {
  return {
    id: layer.id,
    name: layer.name,
    polygons:
      layer.polygons?.map((polygon) => {
        const transparency = polygon.transparency || 30
        const coordinates = polygon.coordinates.map((coordinate) => ({
          latitude: parseFloat(coordinate.latitude),
          longitude: parseFloat(coordinate.longitude)
        }))
        const color = getLayerColor(polygon.color || null)
        return {
          id: polygon.id as string,
          color: color,
          fillColor: [color[0], color[1], color[2], transparency] as TRGBAColor,
          lineColor: [color[0], color[1], color[2], 255] as TRGBAColor,
          lineWidth: polygon.lineWidth || 2,
          transparency: transparency,
          label: polygon.label,
          coordinates,
          contour: polygon.coordinates.map((coordinate) => [
            parseFloat(coordinate.latitude),
            parseFloat(coordinate.longitude)
          ])
        }
      }) || []
  }
}
export const createPolygonsLayersCollection = (
  routingLayers: GetAllActiveRoutingLayersQuery['routingLayers']['items']
): IPolygonsLayersCollection => {
  const polygonsLayers = routingLayers.filter((layer) => layer.type === RoutingLayerType.Polygons)
  return {
    items: polygonsLayers.map((layer) => createPolygonsLayerModel(layer)),
    isEmpty: polygonsLayers.length === 0
  }
}

export const createLocationsLayerModel = (
  layer: GetAllActiveRoutingLayersQuery['routingLayers']['items'][0]
): ILocationsLayerModel => {
  return {
    id: layer.id,
    name: layer.name,
    locationsGroup: {
      id: layer.locationsGroup?.id,
      color: getLayerColor(layer.locationsGroup?.color || null),
      icon: layer.locationsGroup?.icon ? (layer.locationsGroup.icon as ELocationLayerIcon) : ELocationLayerIcon.CIRCLE,
      locations:
        layer.locationsGroup?.locations?.map((location) => {
          const coordinates = {
            latitude: parseFloat(location.coordinates.latitude),
            longitude: parseFloat(location.coordinates.longitude)
          }
          return {
            id: location.id,
            label: location.label,
            coordinates: {
              latitude: coordinates.latitude,
              longitude: coordinates.longitude
            },
            deckCoordinates: [coordinates.longitude, coordinates.latitude]
          }
        }) || []
    }
  }
}

export const createLocationsLayersCollection = (
  routingLayers: GetAllActiveRoutingLayersQuery['routingLayers']['items']
): ILocationsLayersCollection => {
  const locationsLayers = routingLayers.filter((layer) => layer.type === RoutingLayerType.Locations)
  return {
    items: locationsLayers.map((layer) => createLocationsLayerModel(layer)),
    isEmpty: locationsLayers.length === 0
  }
}
